import { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '~/lib/useAuth';
import { authHeaders, getSession, signOut } from '~/lib/auth';
import { triggerPublish } from '~/lib/publish';
import { navGroupsFor, PinnedNav, ReviewBadge } from '~/components/navConfig';

/**
 * Sidebar layout — collapsible groups with state persisted to localStorage.
 *
 * Each group's expanded/collapsed state survives reloads. The group
 * containing the currently-active route is auto-expanded regardless of
 * stored state, so a deep link never lands the user on a "hidden" item.
 */

// Nav groups live in ~/components/navConfig — ONE source shared with the
// Pages editor's overlay drawer so the two navs never drift.
const NAV_GROUPS = navGroupsFor(undefined);

const STORAGE_KEY = 'vv-admin:nav-groups';

function loadOpenState(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function AdminShell() {
  const { user } = useAuth();
  const location = useLocation();

  // Initialize from localStorage; fall back to defaultOpen per group.
  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => {
    const stored = loadOpenState();
    const initial: Record<string, boolean> = {};
    for (const g of NAV_GROUPS) {
      initial[g.label] = stored[g.label] ?? (g.defaultOpen ?? true);
    }
    return initial;
  });

  // Which group owns the current route — used to auto-expand on nav.
  const activeGroup = useMemo(() => {
    return NAV_GROUPS.find((g) =>
      g.items.some((i) =>
        location.pathname === i.to ||
        location.pathname.startsWith(i.to + '/'),
      ),
    )?.label;
  }, [location.pathname]);

  // If the user navigates into a collapsed group, open it.
  useEffect(() => {
    if (!activeGroup) return;
    setOpenMap((prev) => (prev[activeGroup] ? prev : { ...prev, [activeGroup]: true }));
  }, [activeGroup]);

  // Persist preference.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(openMap));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [openMap]);

  function toggle(label: string) {
    setOpenMap((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  // Global publish — fires the CF Workers Builds deploy hook so the
  // public site rebuilds with the latest DB state. Replaces the
  // per-editor auto-publish-on-save (which was wasteful and slow).
  // Status: 'idle' | 'publishing' | 'done' | 'error'. After 'done' we
  // also surface "Published HH:MM" until the next edit.
  const [pubState, setPubState] = useState<'idle' | 'publishing' | 'done' | 'error'>('idle');
  const [pubMsg, setPubMsg] = useState<string | null>(null);
  const [pubAt, setPubAt] = useState<Date | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';
  async function fetchPendingCount() {
    try {
      if (!getSession()) return;
      const res = await fetch(`${API_BASE}/api/admin/publish/pending`, {
        headers: authHeaders(false),
      });
      if (!res.ok) return;
      const data = (await res.json()) as { count: number };
      setPendingCount(data.count);
    } catch {
      /* keep last known count on failure */
    }
  }

  // Refresh count on mount, on route change (likely came from an edit
  // page), and every 30s while open (cheap).
  useEffect(() => { void fetchPendingCount(); }, [location.pathname]);
  useEffect(() => {
    const id = window.setInterval(() => { void fetchPendingCount(); }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  async function publish() {
    if (pubState === 'publishing') return;
    setPubState('publishing');
    setPubMsg(null);
    const r = await triggerPublish();
    if (r.ok) {
      setPubState('done');
      setPubAt(new Date());
      setPubMsg('Live in ~30-60s');
      setPendingCount(0);
      window.setTimeout(() => setPubState('idle'), 8000);
    } else {
      setPubState('error');
      setPubMsg(r.reason ?? 'Publish failed');
    }
  }

  // Publish only makes sense on routes that mutate what the site build
  // reads. Operational pages (Today/Bookings/Clients/Calendar/GLP-1/
  // Money/Team) don't trigger a rebuild — they're internal data only.
  // Editors never see it: the API refuses their deploys. (The Pages
  // editor — pages AND the record lists — is a takeover with its own
  // Publish button.)
  const CONTENT_ROUTES = ['/blocks', '/navigation', '/settings'];
  const showPublish =
    user?.role !== 'editor' &&
    CONTENT_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + '/'));

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand">
          Valley Vitality
          <div className="admin-sidebar__role">Operator</div>
        </div>

        {/* The groups scroll; brand + the pinned footer never do. */}
        <nav className="admin-sidebar__nav">
          {NAV_GROUPS.map((group) => {
            const open = openMap[group.label] ?? true;
            return (
              <div key={group.label} className="admin-sidebar__section">
                <button
                  type="button"
                  className={`admin-sidebar__group${open ? ' is-open' : ''}`}
                  onClick={() => toggle(group.label)}
                  aria-expanded={open}
                >
                  <span className="admin-sidebar__group-leading">
                    <span className="admin-sidebar__group-icon">{group.icon}</span>
                    <span>{group.label}</span>
                  </span>
                  <span className="admin-sidebar__group-chevron" aria-hidden />
                </button>
                {open && (
                  <div className="admin-sidebar__group-items">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `admin-sidebar__link${isActive ? ' is-active' : ''}`
                        }
                      >
                        {item.label}
                        {item.badge === 'review' && <ReviewBadge />}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="admin-sidebar__footer">
          <PinnedNav />
          <div style={{ padding: '8px 12px', fontSize: 13, color: 'rgba(255,255,255,0.78)' }}>
            {user?.email}
          </div>
          <button
            type="button"
            onClick={() => signOut()}
            style={{
              width: '100%',
              padding: '8px 12px',
              textAlign: 'left',
              fontSize: 13,
              color: 'rgba(255,255,255,0.55)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 8,
            }}
          >
            Sign out →
          </button>
          {/* Build stamp — confirms which deploy is loaded. SHA links to
             the commit on GitHub; the timestamp shows when this bundle
             was built. Useful for "did my deploy land?" debugging. */}
          <div
            title={`Built ${__BUILD_TIME__}\nCommit ${__BUILD_SHA__}`}
            style={{
              padding: '6px 12px',
              fontSize: 10.5,
              color: 'rgba(255,255,255,0.30)',
              fontFamily: "'SF Mono', Menlo, Consolas, monospace",
              letterSpacing: '0.02em',
              cursor: 'help',
            }}
          >
            {__BUILD_SHA__} · {new Date(__BUILD_TIME__).toLocaleString('en-US', {
              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            })}
          </div>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar__search">
            <span>🔍</span>
            <span>Search clients, bookings, sheet #s…</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>⌘K</span>
          </div>

          <div style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>

          {showPublish && (
            <>
              {pubMsg && (
                <span style={{
                  fontSize: 12,
                  color: pubState === 'error' ? '#9a3535' : 'var(--text-muted)',
                  whiteSpace: 'nowrap',
                }}>
                  {pubMsg}
                </span>
              )}
              {pubState === 'done' && pubAt && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  Published {pubAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
              <button
                type="button"
                onClick={publish}
                disabled={pubState === 'publishing' || (pubState === 'idle' && pendingCount === 0)}
                title={
                  pendingCount === 0
                    ? 'No pending content changes'
                    : `Push ${pendingCount ?? 'all saved'} content edit${pendingCount === 1 ? '' : 's'} to the live site (~30-60s rebuild)`
                }
                style={{
                  background: pubState === 'done' || pendingCount === 0 ? 'transparent' : 'var(--primary)',
                  color: pubState === 'done' || pendingCount === 0 ? 'var(--text-muted)' : 'var(--card)',
                  border: `1px solid ${pendingCount === 0 ? 'var(--line)' : 'var(--primary)'}`,
                  padding: '7px 14px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: pubState === 'publishing' ? 'wait' :
                          pendingCount === 0 ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'background 120ms, color 120ms',
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                }}
              >
                {pubState === 'publishing' ? 'Publishing…' :
                 pubState === 'done' ? '✓ Published' :
                 pendingCount === 0 ? 'All published' :
                 'Publish'}
                {pubState === 'idle' && pendingCount !== null && pendingCount > 0 && (
                  <span style={{
                    background: 'rgba(255,255,255,0.22)',
                    color: 'inherit',
                    padding: '1px 7px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                  }}>
                    {pendingCount}
                  </span>
                )}
              </button>
            </>
          )}
        </header>
        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
